<template>
  <splide
    ref="splide"
    class="latest-hero-banner critical-component-clear"
    :has-track="false"
    :options="config"
    @splide:drag="handleSplideIsDragging"
    @splide:dragged="handleSplideDragged"
    @splide:move="handleSplideMove"
    @splide:moved="handleSplideMoved"
  >
    <splide-track>
      <splide-slide
        v-for="(banner, index) of bannerData"
        :key="`banner-${index}`"
      >
        <component
          :is="banner.imgVideoUrl && !isMobile ? 'a' : 'div'"
          class="latest-hero-banner__banner"
          :class="{
            'latest-hero-banner__banner--is-dragged': isDragging,
            'latest-hero-banner__banner--has-video': hasVideo(banner)
          }"
          v-bind="banner.imgVideoUrl && !isMobile ? { href: banner.imgVideoUrl } : {}"
        >
          <video-player
            v-if="hasVideo(banner)"
            class="latest-hero-banner__video"
            controls-alignment="right"
            :image="getVideoPreviewImage(banner)"
            :is-active="index === currentIndex"
            :lazyload="banner.useLazyload"
            :use-overlay-button="false"
            :video="getVideo(banner)"
            @click.stop.prevent
          />
          <div
            class="latest-hero-banner__cta-container"
            :class="[
              liquid.ctaPosition
                ? `latest-hero-banner__cta-container--${liquid.ctaPosition}`
                : '',
              liquid.ctaSecondaryPosition
                ? `latest-hero-banner__cta-container--secondary-${liquid.ctaSecondaryPosition}`
                : ''
            ]"
          >
            <transition
              name="fade-up-bottom"
              mode="out-in"
              @after-leave="onTextAfterLeave"
            >
              <div
                v-if="!isTextLeaving"
                :key="currentIndex"
              >
                <btn
                  v-if="banner.ctaLabel && banner.ctaUrl"
                  class="latest-hero-banner__cta"
                  :label="banner.ctaLabel"
                  :modifiers="liquid.cta1ButtonStyle === 'transparent'
                    ? 'transparent'
                    : liquid.cta1ButtonStyle === 'underlined'
                      ? 'underlined'
                      : 'normal'"
                  :url="banner.ctaUrl"
                  :aria-label="banner.ctaLabel"
                  :tabindex="index === currentIndex ? 0 : -1"
                  :aria-hidden="index !== currentIndex"
                  :style="liquid.cta1ButtonStyle === 'underlined'
                    ? {
                      '--btn-underline-color':
                        liquid.ctaUnderlineColor === 'black'
                          ? '#000'
                          : '#fff'
                    }
                    : {}"
                />
                <btn
                  v-if="banner.ctaLabelSecondary && banner.ctaUrlSecondary"
                  class="latest-hero-banner__cta"
                  :label="banner.ctaLabelSecondary"
                  :modifiers="liquid.cta2ButtonStyle === 'transparent'
                    ? 'transparent'
                    : liquid.cta2ButtonStyle === 'underlined'
                      ? 'underlined'
                      : 'normal'"
                  :url="banner.ctaUrlSecondary"
                  :aria-label="banner.ctaLabelSecondary"
                  :tabindex="index === currentIndex ? 0 : -1"
                  :aria-hidden="index !== currentIndex"
                  :style="liquid.cta2ButtonStyle === 'underlined'
                    ? {
                      '--btn-underline-color':
                        liquid.ctaUnderlineColor === 'black'
                          ? '#000'
                          : '#fff'
                    }
                    : {}"
                />
              </div>
            </transition>
          </div>

          <div
            class="latest-hero-banner__media"
            :class="{ 'zoom-out': zoomingIndex === index || previousIndex === index }"
          >
            <div
              class="latest-hero-banner__media-overlay"
            />
            <art-direction
              v-if="!hasVideo(banner)"
              :image="banner.imageMobile"
              :image-desktop="banner.imageDesktop"
              :lazyload="banner.useLazyload"
              :ratio="6/7"
              :ratio-desktop="2/1"
            />
          </div>
          <div
            v-if="showBannerContent(banner)"
            class="container latest-hero-banner__content-container"
          >
            <div class="latest-hero-banner__content col xs4 l3">
              <transition
                name="fade-up-bottom"
                mode="out-in"
                @after-leave="onTextAfterLeave"
              >
                <div
                  v-if="!isTextLeaving"
                  :key="currentIndex"
                >
                  <h2
                    class="text-heading-m latest-hero-banner__title"
                    :style="{
                      color: banner.titleCopyColor === 'black'
                        ? '#000'
                        : '#fff'
                    }"
                    v-text="banner.title"
                  />
                  <p
                    class="latest-hero-banner__copy"
                    :style="{
                      color: banner.titleCopyColor === 'black'
                        ? '#000'
                        : '#fff'
                    }"
                    v-text="banner.copy"
                  />
                </div>
              </transition>
            </div>
          </div>
        </component>
      </splide-slide>
    </splide-track>
    <div
      v-if="bannerData.length > 1"
      class="latest-hero-banner__controls"
    >
      <div
        class="splide__toggle-wrapper"
        :class="{'splide__toggle-wrapper--hidden': hideSplideToggle}"
      >
        <ul class="latest-hero-banner__pagination">
          <li
            v-for="(banner, index) of bannerData"
            :key="`banner-${index}`"
          >
            <button
              class="latest-hero-banner__pagination-button splide__pagination__page"
              :class="{ 'splide__pagination__page--active': currentIndex === index }"
              type="button"
              :aria-current="Boolean(currentIndex === index)"
              :style="currentIndex === index
                ? { backgroundColor: '#fff' }
                : { backgroundColor: liquid.slideColor === 'black' ? '#000' : '#A9A9A9' }"
              @click.prevent="handlePaginationClick(index)"
            >
              <span
                class="visually-hidden"
                v-text="$string(
                  'accessibility.carousel.go_to_slide',
                  { replace: { slide: index + 1 } }
                )"
              />

              <span
                v-if="currentIndex === index"
                class="latest-hero-banner__pagination-icon"
              />
            </button>
          </li>
        </ul>
        <button
          class="splide__toggle button button--text"
          type="button"
          @click.stop
        >
          <span class="splide__toggle__play">
            <icon-play />
          </span>

          <span class="splide__toggle__pause">
            <icon-pause />
          </span>
        </button>
      </div>
    </div>
  </splide>
</template>

<script>
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapState } from 'vuex'

import IconBrandLogo from '~icons/general/brand-logo.svg'
import IconCross2 from '~icons/general/cross-2.svg'
import IconCross from '~icons/general/cross.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'

import VideoPlayer from '~async/video-player/video-player'
import Btn from '~global/btn/btn'
import ArtDirection from '~global/images/art-direction'

export default {
  name: 'LatestHeroBanner',

  components: {
    ArtDirection,
    Btn,
    IconBrandLogo,
    IconCross,
    IconCross2,
    IconPause,
    IconPlay,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
  },

  props: {
    bannerData: {
      type: Array,
      default: () => [],
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        autoplayInterval: 5000,
        rotationSpeed: 300,
      }),
      cta1ButtonStyle: {
        type: String,
        default: 'transparent',
      },
      cta2ButtonStyle: {
        type: String,
        default: 'transparent',
      },
      ctaPosition: {
        type: String,
        default: 'top-left',
      },
      slideColor: {
        type: String,
        default: 'dark grey',
      },
      ctaUnderlineColor: {
        type: String,
        default: 'white',
      },
    },

  },

  data() {
    return {
      config: {
        arrows: false,
        autoplay: true,
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        interval: this.liquid.autoplayInterval,
        mediaQuery: 'min',
        pagination: false,
        speed: this.liquid.rotationSpeed,
        start: 0,
        rewind: true,
        type: 'fade',
      },
      currentIndex: 0,
      hideSplideToggle: true,
      isDragging: false,
      zoomingIndex: null,
      previousIndex: null,
      pendingIndex: null,
      isTextLeaving: false,

    }
  },

  computed: {
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },
  mounted() {
    this.currentIndex = 0
    this.pendingIndex = null
    this.isTextLeaving = false
    this.hideSplideToggle = false
  },
  methods: {
    onTextAfterLeave() {
      if (this.pendingIndex === null) {
        this.isTextLeaving = false
        this.hideSplideToggle = false
      } else {
        if (this.$refs.splide && this.$refs.splide.splide) {
          this.$refs.splide.splide.Components.Autoplay.pause()
        }
        this.previousIndex = this.currentIndex
        this.currentIndex = this.pendingIndex
        this.pendingIndex = null
        this.$refs.splide.go(this.currentIndex)
        this.$nextTick().then(() => {
          if (this.$refs.splide && this.$refs.splide.splide) {
            this.$refs.splide.splide.Components.Autoplay.play()
          }
          this.isTextLeaving = false
          this.hideSplideToggle = false
        })
      }
    },
    getVideo(banner) {
      return this.isMobile
        ? this.getVideoSource(banner.videoMobile.video.sources)
        : this.getVideoSource(banner.videoDesktop.video.sources)
    },
    getVideoPreviewImage(banner) {
      return this.isMobile
        ? banner.videoMobile.preview_image
        : banner.videoMobile.preview_image
    },
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },
    hasVideo(banner) {
      return banner.videoDesktop || banner.videoMobile
    },
    handleSplideMove(splide, newIndex) {
      if (!this.isTextLeaving) {
        this.pendingIndex = newIndex
        this.isTextLeaving = true
        this.hideSplideToggle = true
        if (splide && splide.splide && splide.splide.Components.Autoplay) {
          splide.splide.Components.Autoplay.pause()
        }

        return
      }
      this.previousIndex = this.currentIndex
      this.zoomingIndex = newIndex
    },
    handleSplideMoved(_, newIndex) {
      const bannerLength = this.bannerData?.length
      if (newIndex >= bannerLength) {
        this.pendingIndex = 0
        this.isTextLeaving = true
        this.hideSplideToggle = true
        if (this.$refs.splide && this.$refs.splide.splide.Components.Autoplay) {
          this.$refs.splide.splide.Components.Autoplay.pause()
        }
        this.$nextTick().then(() => {
          this.onTextAfterLeave()
        })
      } else {
        this.currentIndex = newIndex
        this.zoomingIndex = newIndex
        this.previousIndex = null
        this.isTextLeaving = false
        this.pendingIndex = null
      }
      this.hideSplideToggle = false
    },
    handlePaginationClick(index = 0) {
      if (index === this.currentIndex) {
        return
      }
      this.pendingIndex = index
      this.isTextLeaving = true
      this.hideSplideToggle = true
    },
    handleSplideIsDragging() {
      this.setIsDragging(true)
    },
    handleSplideDragged() {
      this.setIsDragging(false)
    },
    setIsDragging(isDragging) {
      this.isDragging = isDragging
    },
    showBannerContent(banner) {
      const hasContent = banner.title ||
        banner.copy ||
        (banner.ctaLabel && banner.ctaUrl) ||
        (banner.ctaLabelSecondary && banner.ctaUrlSecondary)

      return this.bannerData.length > 1 || hasContent
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './latest-hero-banner';
</style>
